<script setup>
import { computed, ref, reactive } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { ValidationError } from '@/errors/ValidationError';

import { XSpinner, XFileUpload, XHelperMessage } from '@clicksign/excalibur';

const props = defineProps({
  path: {
    type: String,
    default: '',
  },
  loadingText: {
    type: String,
    default: '',
  },
  isRenew: Boolean,
});

const emit = defineEmits([
  'button-click',
  'document-with-tsa',
  'document-validated',
  'document-resigned',
]);

const store = useStore();
const { t } = useI18n();

const isUploading = ref(false);
const documentValidatorErrors = ref([]);

const documentAcceptedTypes = ['application/pdf'];

const documentMaxFileSize = computed(() => {
  return Number(process.env.DOCUMENT_MAX_FILE_SIZE) || 100;
});

const messagesList = reactive({
  invalidFileType: {
    message: t('validator.document.form.invalid.file_type'),
  },
  invalidFileSize: {
    message: t('validator.document.form.invalid.file_size', {
      file_max_size: documentMaxFileSize.value,
    }),
  },
  unexpected: {
    message: t('general.errors.unexpected'),
  },
  restrictedFile: {
    message: t('general.errors.restrictedFile'),
  },
  invalidFile: {
    message: t('general.errors.invalidFile'),
  },
});

const fileMaxSizeValue = computed(() => {
  return documentMaxFileSize.value * Math.floor(1024 * 1024);
});

function handleFileError(e) {
  if (e?.reason === 'empty_file') {
    documentValidatorErrors.value.push(messagesList.invalidFile);
  } else if (e?.reason === 'file_type_not_allowed') {
    documentValidatorErrors.value.push(messagesList.invalidFileType);
  } else if (e?.reason === 'file_size_exceeded') {
    documentValidatorErrors.value.push(messagesList.invalidFileSize);
  }
}

function handleFileChange(files) {
  const [file] = files;

  submitDocument(file);
}

function documentReader(file) {
  const reader = new FileReader();

  reader.readAsDataURL(file);
}

async function submitDocument(document) {
  documentValidatorErrors.value = [];

  if (document === undefined) {
    documentValidatorErrors.value.push(messagesList.unexpected);
    return;
  }

  isUploading.value = true;

  const fileSize = document.size;
  const fileType = document.type;
  const fileName = document.name;

  documentReader(document, fileSize, fileType);

  const formData = new FormData();

  formData.append('document', document);

  try {
    const payload = {
      formData,
      path: props.path,
      fileName,
    };

    const response = await store.dispatch('fileUpload/documentValidator', payload);

    if (!response.signed_by_clicksign) return;

    if (props.isRenew && response.with_tsa) {
      emit('document-with-tsa');
    } else if (!response.data) {
      emit('document-validated', true);
    } else {
      emit('document-resigned', response);
    }
  } catch (error) {
    if (!(error instanceof ValidationError)) {
      documentValidatorErrors.value.push(messagesList.unexpected);

      throw error;
    }

    const { errors, isEncrypted } = error.cause;

    if (isEncrypted) {
      documentValidatorErrors.value.push(messagesList.restrictedFile);
    } else if (errors.length > 0) {
      errors.forEach((error) => {
        documentValidatorErrors.value.push({
          message: error,
        });
      });
    }
  } finally {
    isUploading.value = false;
  }
}
</script>

<template>
  <div :class="$style.baseFileUpload">
    <div
      v-if="!isUploading"
      :class="$style.container"
    >
      <XFileUpload
        :title="t('general.action.uploadTitle')"
        :description="t('general.action.uploadDescription')"
        :accept="documentAcceptedTypes"
        data-testid="documentInputField"
        :multiple="false"
        :max-size-mb="fileMaxSizeValue"
        @input:file="handleFileChange"
        @input:file:error="handleFileError"
      />
      <XHelperMessage :additional-messages="documentValidatorErrors" />
    </div>
    <XSpinner
      v-else
      show-text
    >
      {{ loadingText }}
    </XSpinner>
  </div>
</template>

<style lang="scss" module>
.baseFileUpload {
  display: flex;
  justify-content: center;
  width: 100%;

  .container {
    display: flex;
    align-items: center;
    flex-direction: column;
    width: 100%;
    gap: var(--space-medium);
  }
}
</style>
